import { switchMap } from 'rxjs/operators'
import { ActivatedRoute, Router } from '@angular/router'
import { AngularFireAuth } from '@angular/fire/compat/auth'
import { Injectable, EventEmitter, NgZone } from '@angular/core'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import { Observable, of, Subscription } from 'rxjs'
import { AppUser } from '../models/app-user.model'
import { UserService } from './user.service'
import { RouteNames } from '../const/route/route-names.model'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userLoggedinEvent: EventEmitter<boolean> = new EventEmitter<boolean>()
  user$: Observable<firebase.User>
  appUser: AppUser
  private profileSubscription?: Subscription

  constructor(
    private userService: UserService,
    private afAuth: AngularFireAuth,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone
  ) {
    this.user$ = afAuth.authState
    firebase.auth().onAuthStateChanged((user: firebase.User | null) => {
      this.ngZone.run(() => {
        if (user) {
          this.updateAppUser(user)
          this.router.navigate(['/' + RouteNames.PATH_EVENT])
        } else {
          this.clearCurrentUser()
        }
      })
    })
  }

  private updateAppUser(user: firebase.User) {
    this.profileSubscription?.unsubscribe()
    this.profileSubscription = this.userService.get(user.uid).subscribe((appUser: AppUser | null) => {
      this.appUser = appUser
      this.userLoggedinEvent.emit(!!appUser)
    })
  }

  login() {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/'
    localStorage.setItem('returnUrl', returnUrl)
  }

  isLoggedIn(): boolean {
    return this.appUser ? true : false
  }

  signInWithGoogle() {
    let provider = new firebase.auth.GoogleAuthProvider()
    firebase
      .auth()
      .signInWithPopup(provider)
      .then(result => {
        let credential = result.credential
        let user = result.user
      })
      .catch(error => {
        let errorCode = error.code
        let errorMessage = error.message
        let email = error.email
        let credential = error.credential
      })
  }

  signInWithEmailAndPassword(email: string, password: string) {
    return this.afAuth.signInWithEmailAndPassword(email, password)
  }

  createUserWithEmailAndPassword(name: string, email: string, password: string) {
    return this.afAuth.createUserWithEmailAndPassword(email, password).then(cred => {
      if (cred.user) {
        return cred.user.updateProfile({ displayName: name }).then(() => {
          this.userService.save(cred.user)
          return cred
        })
      }
      return cred
    })
  }

  async reauthenticateForDeletion(password: string): Promise<void> {
    const user = await this.afAuth.currentUser
    if (!user?.email) {
      throw { code: 'auth/user-token-expired' }
    }
    if (!password) {
      throw { code: 'auth/missing-password' }
    }

    const credential = firebase.auth.EmailAuthProvider.credential(user.email, password)
    await user.reauthenticateWithCredential(credential)
    await user.getIdToken(true)
  }

  async signOut(): Promise<void> {
    await this.afAuth.signOut()
    this.clearCurrentUser()
  }

  async logout(): Promise<void> {
    await this.signOut()
    await this.router.navigate(['/login'])
  }

  private clearCurrentUser(): void {
    this.profileSubscription?.unsubscribe()
    this.profileSubscription = undefined
    this.appUser = null
    this.userLoggedinEvent.emit(false)
  }

  getCurrentUser(): AppUser {
    return this.appUser
  }

  get appUser$(): Observable<AppUser> {
    return this.user$.pipe(
      switchMap((user: firebase.User) => {
        if (user) {
          return this.userService.get(user.uid)
        }
        return of(null)
      })
    )
  }

  // New methods for password reset
  sendPasswordResetEmail(email: string, url: string): Promise<void> {
    return this.afAuth.sendPasswordResetEmail(email, { url: url })
  }

  verifyPasswordResetCode(oobCode: string): Promise<string> {
    return firebase.auth().verifyPasswordResetCode(oobCode)
  }

  confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return this.afAuth.confirmPasswordReset(oobCode, newPassword)
  }
}
